import {Pinia} from 'pinia'
import {RouteLocationNormalized} from 'vue-router'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {useResourceItemStore} from '@toolify/client/src/stores/ResourceItemStore/useResourceItemStore'
import {useSpaceStore} from '@toolify/client/src/stores/SpaceStore/useSpaceStore'
import {useCollectionStore} from '@toolify/client/src/stores/CollectionStore/useCollectionStore'
import {useMinimizedItemStore} from '@toolify/client/src/stores/MinimizedItemStore/useMinimizedItemStore'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {ExperimentalFeatureFlagType} from '@toolify/server/src/models/mongoose/UserModel/enum/ExperimentalFeatureFlagType'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IUserResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {LocalPreferenceType} from '@toolify/client/src/stores/LocalPreferenceStore/enum/LocalPreferenceType'
import {useLocalPreferenceStore} from '@toolify/client/src/stores/LocalPreferenceStore/useLocalPreferenceStore'
import {ApiException} from '@toolify/server/src/middleware/ApiResponseMiddleware/errors/ApiException'
import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'
import {isStackableOverlayNavigation, isStackableRoute} from './isStackableOverlayNavigation'
import {getUnavailableWorkspaceRedirect} from './getUnavailableWorkspaceRedirect'
import * as Sentry from '@sentry/vue'

export function syncSpaceFromRouteRouterHookFactory(pinia: Pinia) {
  return async function syncSpaceFromRouteRouterHook(to: RouteLocationNormalized, from: RouteLocationNormalized) {
    const isOverlayNavigation = isStackableOverlayNavigation(to, from)
    const workspaceName = to.params.workspaceName as string | undefined
    if(!workspaceName) {
      return
    }
    const workspaceStore = useWorkspaceStore(pinia)
    const resourceItemStore = useResourceItemStore(pinia)
    const spaceStore = useSpaceStore(pinia)
    const collectionStore = useCollectionStore(pinia)
    const authStore = useAuthStore(pinia)
    const localPreferenceStore = useLocalPreferenceStore(pinia)
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return
    }
    const currentUser = authStore.currentEntity?.entityType === EntityType.User
      ? authStore.currentEntity.payload as IUserResponseInstance
      : null
    const isCollectionsEnabled = currentUser?.experimentalFeatureFlags?.includes(
      ExperimentalFeatureFlagType.EnableCollections,
    ) === true

    const clearLastVisitedResource = async (spaceIds: string[] = []): Promise<void> => {
      if(!workspaceName) {
        return
      }
      await Promise.all([
        localPreferenceStore.remove(LocalPreferenceType.LastVisitedResource, workspaceName),
        ...spaceIds.map(spaceId => localPreferenceStore.remove(LocalPreferenceType.LastVisitedResourceBySpaceId, spaceId)),
      ])
    }

    // the bar of minimized records never holds up navigation, and a failed list only leaves it empty
    const minimizedItemStore = useMinimizedItemStore(pinia)
    if(!minimizedItemStore.hasLoaded(workspaceId)) {
      minimizedItemStore.fetchMinimizedItems(workspaceId).catch(error => Sentry.captureException(error))
    }

    try {
      const pending: Promise<unknown>[] = []
      if(!resourceItemStore.hasLoaded) {
        pending.push(resourceItemStore.fetchResourceItems(workspaceId))
        pending.push(resourceItemStore.fetchResourceGroups(workspaceId))
      }
      if(!spaceStore.hasResourceSpacesLoaded(workspaceId)) {
        pending.push(spaceStore.fetchResourceSpaces(workspaceId))
      }
      if(isCollectionsEnabled && !collectionStore.hasCollectionItemsLoaded(workspaceId)) {
        pending.push(collectionStore.fetchCollectionItems(workspaceId))
      }
      if(pending.length > 0) {
        await Promise.all(pending)
      }
    } catch (e) {
      if(isOverlayNavigation) {
        return
      }
      if(e instanceof ApiException && e.name === KnownError.NotFound) {
        await clearLastVisitedResource()
        if(to.name === RouteName.WorkspaceHome) {
          return getUnavailableWorkspaceRedirect(workspaceName)
        }
        return {
          name: RouteName.WorkspaceHome,
          params: {workspaceName},
        }
      }
      Sentry.captureException(e)
      return getUnavailableWorkspaceRedirect(workspaceName)
    }
    if(isOverlayNavigation) {
      return
    }
    const resourceSpaces = spaceStore.resourceSpaces
    if(!resourceSpaces?.length) {
      return
    }

    const spaceIds = resourceSpaces.map((s) => s.id)
    const setSpace = (spaceId: string) => {
      spaceStore.setCurrentSpaceId(workspaceId, spaceId)
      void localPreferenceStore.set(LocalPreferenceType.LastVisitedSpaceId, spaceId, workspaceId)
    }

    const routeResource = to.meta.determineResource?.(to) ?? null
    if(routeResource && resourceItemStore.resourceItems) {
      const routeResourceIds = new Set(
        Object.values(to.params)
          .flatMap(param => Array.isArray(param) ? param : [param])
          .filter((param): param is string => typeof param === 'string'),
      )
      routeResourceIds.add(routeResource.resourceId)
      const resourceItem = resourceItemStore.resourceItems.find(
        (item) =>
          item.resourceEntity.resourceId === routeResource.resourceId &&
          item.resourceEntity.resourceType === routeResource.resourceType,
      ) ?? resourceItemStore.resourceItems.find(
        (item) => routeResourceIds.has(String(item.resourceEntity.resourceId)),
      )
      if(!resourceItem) {
        if(routeResource.resourceType === ResourceType.Automation) {
          return
        }
        if(isStackableRoute(to)) {
          return
        }
        await clearLastVisitedResource(spaceIds)
        if(workspaceName) {
          return {
            name: RouteName.WorkspaceHome,
            params: {workspaceName},
          }
        }
      }
      if(resourceItem?.resourceSpaceId && spaceIds.includes(resourceItem.resourceSpaceId)) {
        setSpace(resourceItem.resourceSpaceId)
      }
      return
    }

    const spaceIdFromQuery = typeof to.query.spaceId === 'string' ? to.query.spaceId : null
    if(spaceIdFromQuery && spaceIds.includes(spaceIdFromQuery)) {
      setSpace(spaceIdFromQuery)
      return
    }

    const currentSpaceId = spaceStore.currentSpaceId
    const lastVisitedSpaceId = localPreferenceStore.get(LocalPreferenceType.LastVisitedSpaceId, workspaceId)
    const accessibleSpaceIds = new Set(
      (resourceItemStore.resourceItems ?? [])
        .map((resourceItem) => resourceItem.resourceSpaceId)
        .filter((resourceSpaceId): resourceSpaceId is string => !!resourceSpaceId),
    )
    const firstAccessibleSpaceId = spaceIds.find((id) => accessibleSpaceIds.has(id)) ?? spaceIds[0]
    const selectedSpaceId =
      currentSpaceId && spaceIds.includes(currentSpaceId)
        ? currentSpaceId
        : lastVisitedSpaceId && spaceIds.includes(lastVisitedSpaceId)
          ? lastVisitedSpaceId
          : firstAccessibleSpaceId
    setSpace(selectedSpaceId)
  }
}
